import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { decodeClaims, isExpired, type LegacyClaims } from '@/lib/auth';
import { useUsersStore } from './users-store';
import { useVehiclesStore } from './vehicles-store';
import { useEventsStore } from './events-store';
import { queryClient } from '@/lib/query-client';

// =============================================================================
// Auth store — JWT token + claims + impersonation state.
// Di-persist ke localStorage agar refresh halaman tidak hilang sesi.
// =============================================================================

interface AuthState {
  token: string | null;
  claims: LegacyClaims | null;
  /**
   * viewAsUserId: ID user yang sedang di-impersonate (cuma untuk admin/reseller).
   * Kalau null → pakai user sendiri (claims.id).
   */
  viewAsUserId: number | null;
  /** Nama user yang sedang di-impersonate (untuk display di topbar). */
  viewAsUserName: string | null;

  /** Set token baru setelah login sukses. Auto-decode claims. */
  setToken: (token: string) => void;
  /** Hapus semua auth state. Dipanggil saat logout atau 401 dari API. */
  logout: () => void;
  /** Pilih user untuk di-impersonate. Pass null untuk balik ke user sendiri. */
  setViewAsUser: (id: number | null, name?: string | null) => void;
  /** Backward compat — alias yang hanya set id (name kosong). */
  setViewAsUserId: (id: number | null) => void;
  /** User ID yang efektif untuk API calls (viewAsUserId kalau ada, else claims.id). */
  effectiveUserId: () => number | null;
  /** Cek apakah user boleh switch ke user lain (level_id 1 admin / 4 reseller). */
  canSwitchUser: () => boolean;
  /** Cek token masih valid (ada + belum expire). */
  isAuthenticated: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      claims: null,
      viewAsUserId: null,
      viewAsUserName: null,

      setToken: (token) => {
        const claims = decodeClaims(token);
        // Bersihkan impersonation kalau user baru bukan admin/reseller (mis.
        // sisa viewAsUserId di localStorage dari sesi sebelumnya).
        const canSwitch = claims?.level_id === 1 || claims?.level_id === 4;
        set({
          token,
          claims,
          ...(canSwitch ? {} : { viewAsUserId: null, viewAsUserName: null }),
        });
      },

      logout: () => {
        // Clear semua cache supaya tidak ada sisa data user sebelumnya.
        useUsersStore.getState().clear();
        useVehiclesStore.getState().reset([]);
        useEventsStore.getState().clear();
        queryClient.clear();
        set({ token: null, claims: null, viewAsUserId: null, viewAsUserName: null });
      },

      // Impersonation HANYA untuk admin(1)/reseller(4). Untuk level lain,
      // permintaan switch diabaikan (paksa null) — proteksi tombol switch-user
      // di topbar/map bukan sekadar sembunyikan UI, tapi juga di level state.
      setViewAsUser: (id, name) => {
        const level = get().claims?.level_id;
        if (level !== 1 && level !== 4) {
          set({ viewAsUserId: null, viewAsUserName: null });
          return;
        }
        set({ viewAsUserId: id, viewAsUserName: id == null ? null : (name ?? null) });
      },

      setViewAsUserId: (id) => {
        const level = get().claims?.level_id;
        if (level !== 1 && level !== 4) {
          set({ viewAsUserId: null, viewAsUserName: null });
          return;
        }
        set({ viewAsUserId: id, viewAsUserName: id == null ? null : get().viewAsUserName });
      },

      effectiveUserId: () => {
        const s = get();
        return s.viewAsUserId ?? s.claims?.id ?? null;
      },

      canSwitchUser: () => {
        const level = get().claims?.level_id;
        return level === 1 || level === 4;
      },

      isAuthenticated: () => {
        const t = get().token;
        return !!t && !isExpired(t);
      },
    }),
    {
      name: 'webgps-auth',
      // Hanya persist field stateful, bukan function.
      partialize: (s) => ({
        token: s.token,
        claims: s.claims,
        viewAsUserId: s.viewAsUserId,
        viewAsUserName: s.viewAsUserName,
      }),
    },
  ),
);
